import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  Snackbar,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Tab,
  Tabs,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LinkOff as LinkOffIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { licensingAPI, apiService, License, LicenseLink, LicenseAuditEntry } from '../services/api';
import LicenseStatusBadge from '../components/common/LicenseStatusBadge';

const LicenseDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [license, setLicense] = useState<License | null>(null);
  const [entities, setEntities] = useState<LicenseLink[]>([]);
  const [audit, setAudit] = useState<LicenseAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    expiry_date: null as Date | null,
    notes: '',
  });

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Batch unlink
  const [selectedEntities, setSelectedEntities] = useState<Set<string>>(new Set());
  const [unlinking, setUnlinking] = useState(false);
  const [entitySearch, setEntitySearch] = useState('');

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    if (id) {
      loadLicense();
      loadEntities();
      loadAudit();
    }
  }, [id]);

  const loadLicense = async () => {
    try {
      setLoading(true);
      const { license: data } = await licensingAPI.get(id!);
      setLicense(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load license');
    } finally {
      setLoading(false);
    }
  };

  const loadEntities = async () => {
    try {
      const { entities: data } = await licensingAPI.getEntities(id!);
      setEntities(data);
    } catch {
      setEntities([]);
    }
  };

  const loadAudit = async () => {
    try {
      const { audit: data } = await licensingAPI.getAudit(id!);
      setAudit(data);
    } catch {
      setAudit([]);
    }
  };

  const handleEdit = () => {
    if (!license) return;
    setEditForm({
      name: license.name,
      expiry_date: license.expiry_date ? new Date(license.expiry_date) : null,
      notes: license.notes || '',
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!license) return;
    try {
      setSaving(true);
      const updateData: Record<string, any> = {};
      if (editForm.name.trim() !== license.name) {
        updateData.name = editForm.name.trim();
      }
      const newExpiry = editForm.expiry_date ? format(editForm.expiry_date, 'yyyy-MM-dd') : null;
      if (newExpiry !== (license.expiry_date || null)) {
        if (newExpiry) {
          updateData.expiry_date = newExpiry;
        } else {
          updateData.clear_expiry = true;
        }
      }
      const newNotes = editForm.notes.trim() || null;
      if (newNotes !== (license.notes || null)) {
        if (newNotes) {
          updateData.notes = newNotes;
        } else {
          updateData.clear_notes = true;
        }
      }

      if (Object.keys(updateData).length > 0) {
        await licensingAPI.update(id!, updateData);
        setSnackbarMessage('License updated');
        setSnackbarOpen(true);
        await loadLicense();
        await loadAudit();
      }
      setEditDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update license');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await licensingAPI.delete(id!);
      setSnackbarMessage('License deleted');
      setSnackbarOpen(true);
      navigate('/licenses');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete license');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleEntity = (entityId: string) => {
    setSelectedEntities(prev => {
      const next = new Set(prev);
      if (next.has(entityId)) {
        next.delete(entityId);
      } else {
        next.add(entityId);
      }
      return next;
    });
  };

  const filteredEntities = entities.filter((e) => {
    if (!entitySearch) return true;
    return e.entity_id.toLowerCase().includes(entitySearch.toLowerCase());
  });

  const handleSelectAll = () => {
    if (selectedEntities.size === filteredEntities.length) {
      setSelectedEntities(new Set());
    } else {
      setSelectedEntities(new Set(filteredEntities.map(e => e.entity_id)));
    }
  };

  const handleBatchUnlink = async () => {
    if (selectedEntities.size === 0) return;
    try {
      setUnlinking(true);
      const toUnlink = new Set(selectedEntities);
      for (const entityId of toUnlink) {
        await licensingAPI.unlink(id!, entityId);
      }
      setEntities(prev => prev.filter(e => !toUnlink.has(e.entity_id)));
      setSnackbarMessage(`Unlinked ${toUnlink.size} item(s)`);
      setSnackbarOpen(true);
      setSelectedEntities(new Set());
      await loadAudit();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlink');
      await loadEntities();
    } finally {
      setUnlinking(false);
    }
  };

  if (loading) return <LinearProgress />;
  if (!license) return <Alert severity="error">License not found</Alert>;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate('/licenses')}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5">{license.name}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
              <Chip label={license.license_type} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
              <LicenseStatusBadge status={license.status} />
              {license.days_until_expiry !== null && license.days_until_expiry !== undefined && (
                <Typography variant="caption" color="text.secondary">
                  {license.days_until_expiry > 0
                    ? `${license.days_until_expiry} days remaining`
                    : license.days_until_expiry < 0
                    ? `Expired ${Math.abs(license.days_until_expiry)} days ago`
                    : 'Expires today'}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
              Edit
            </Button>
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteDialogOpen(true)}>
              Delete
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Info Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Expiry Date</Typography>
                <Typography>{license.expiry_date || 'No expiry set'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Created</Typography>
                <Typography>{license.created_at ? new Date(license.created_at).toLocaleDateString() : '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Created By</Typography>
                <Typography>{license.created_by || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Linked Entities</Typography>
                <Typography>{entities.length}</Typography>
              </Box>
            </Box>
            {license.notes && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Notes</Typography>
                <Typography>{license.notes}</Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Tabs: Linked Entities / Audit Trail */}
        <Card>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label={`Linked ${license.license_type === 'song' ? 'Ads' : 'Productions'} (${entities.length})`} />
            <Tab label={`Audit Trail (${audit.length})`} />
          </Tabs>

          {activeTab === 0 && (
            <>
              {entities.length > 0 && (
                <Box sx={{ px: 2, pt: 1.5 }}>
                  <TextField
                    size="small"
                    placeholder="Search linked items..."
                    value={entitySearch}
                    onChange={(e) => setEntitySearch(e.target.value)}
                    fullWidth
                  />
                </Box>
              )}
              {filteredEntities.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                  <Checkbox
                    checked={selectedEntities.size === filteredEntities.length && filteredEntities.length > 0}
                    indeterminate={selectedEntities.size > 0 && selectedEntities.size < filteredEntities.length}
                    onChange={handleSelectAll}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {selectedEntities.size > 0 ? `${selectedEntities.size} selected` : `Select all (${filteredEntities.length})`}
                  </Typography>
                  {selectedEntities.size > 0 && (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<LinkOffIcon />}
                      onClick={handleBatchUnlink}
                      disabled={unlinking}
                    >
                      {unlinking ? 'Unlinking...' : `Unlink (${selectedEntities.size})`}
                    </Button>
                  )}
                </Box>
              )}
              <List>
                {filteredEntities.length === 0 ? (
                  <ListItem>
                    <ListItemText
                      primary={entitySearch ? 'No items match your search' : `No ${license.license_type === 'song' ? 'ads' : 'productions'} linked yet`}
                      primaryTypographyProps={{ color: 'text.secondary' }}
                    />
                  </ListItem>
                ) : (
                  filteredEntities.map((entity) => (
                    <ListItem
                      key={`${entity.license_id}-${entity.entity_id}`}
                      divider
                      sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <Checkbox
                        checked={selectedEntities.has(entity.entity_id)}
                        onChange={() => handleToggleEntity(entity.entity_id)}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <ListItemText
                        primary={entity.entity_id}
                        secondary={`Linked by ${entity.linked_by} on ${new Date(entity.linked_at).toLocaleDateString()}`}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (entity.entity_type === 'ad') {
                            navigate(`/ads/${entity.entity_id}`);
                          } else {
                            navigate(`/productions/${entity.entity_id}`);
                          }
                        }}
                      />
                      <Chip label={entity.entity_type} size="small" variant="outlined" sx={{ mr: 3 }} />
                      <ChevronRightIcon
                        color="action"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (entity.entity_type === 'ad') {
                            navigate(`/ads/${entity.entity_id}`);
                          } else {
                            navigate(`/productions/${entity.entity_id}`);
                          }
                        }}
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </>
          )}

          {activeTab === 1 && (
            <List>
              {audit.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No audit entries" primaryTypographyProps={{ color: 'text.secondary' }} />
                </ListItem>
              ) : (
                audit.map((entry, index) => (
                  <ListItem key={`${entry.timestamp}-${index}`} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip label={entry.action} size="small" variant="outlined" />
                          <Typography variant="body2">by {entry.changed_by}</Typography>
                          {entry.entity_id && (
                            <Typography variant="caption" color="text.secondary">
                              ({entry.entity_id})
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={new Date(entry.timestamp).toLocaleString()}
                    />
                  </ListItem>
                ))
              )}
            </List>
          )}
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit License</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="License Name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
                fullWidth
              />
              <DatePicker
                label="Expiry Date"
                value={editForm.expiry_date}
                onChange={(date) => setEditForm({ ...editForm, expiry_date: date })}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <TextField
                label="Notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} variant="contained" disabled={saving || !editForm.name.trim()}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete License</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "<strong>{license.name}</strong>"?
            </Typography>
            {entities.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This license is linked to {entities.length} {license.license_type === 'song' ? 'ad' : 'production'}(s).
                All links will be removed.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default LicenseDetailPage;
